import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'

type ToastKind = 'success' | 'error' | 'info'

type Toast = {
  id: number
  kind: ToastKind
  message: string
}

type ConfirmOptions = {
  title: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  danger?: boolean
}

type PromptOptions = {
  title: string
  message?: string
  placeholder?: string
  confirmLabel?: string
  cancelLabel?: string
}

type AdminUiContextValue = {
  toast: {
    success: (message: string) => void
    error: (message: string) => void
    info: (message: string) => void
  }
  confirm: (options: ConfirmOptions) => Promise<boolean>
  prompt: (options: PromptOptions) => Promise<string | null>
}

const AdminUiContext = createContext<AdminUiContextValue | null>(null)

export function useAdminUi() {
  const ctx = useContext(AdminUiContext)
  if (!ctx) throw new Error('useAdminUi must be used within AdminUiProvider')
  return ctx
}

export default function AdminUiProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const [toasts, setToasts] = useState<Toast[]>([])
  const [confirmState, setConfirmState] = useState<(ConfirmOptions & { open: true }) | null>(null)
  const [promptState, setPromptState] = useState<(PromptOptions & { open: true; value: string }) | null>(
    null,
  )
  const confirmRef = useRef<(value: boolean) => void>(() => {})
  const promptRef = useRef<(value: string | null) => void>(() => {})
  const toastId = useRef(0)

  const pushToast = useCallback((kind: ToastKind, message: string) => {
    const id = ++toastId.current
    setToasts((prev) => [...prev, { id, kind, message }])
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((row) => row.id !== id))
    }, 4200)
  }, [])

  const toast = useMemo(
    () => ({
      success: (message: string) => pushToast('success', message),
      error: (message: string) => pushToast('error', message),
      info: (message: string) => pushToast('info', message),
    }),
    [pushToast],
  )

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      confirmRef.current = resolve
      setConfirmState({ ...options, open: true })
    })
  }, [])

  const prompt = useCallback((options: PromptOptions) => {
    return new Promise<string | null>((resolve) => {
      promptRef.current = resolve
      setPromptState({ ...options, open: true, value: '' })
    })
  }, [])

  function closeConfirm(result: boolean) {
    setConfirmState(null)
    confirmRef.current(result)
  }

  function closePrompt(result: string | null) {
    setPromptState(null)
    promptRef.current(result)
  }

  const value = useMemo(() => ({ toast, confirm, prompt }), [toast, confirm, prompt])

  return (
    <AdminUiContext.Provider value={value}>
      {children}
      <div className="vc-admin-toast-stack" aria-live="polite">
        {toasts.map((row) => (
          <div key={row.id} className={`vc-admin-toast vc-admin-toast--${row.kind}`}>
            {row.message}
          </div>
        ))}
      </div>
      {confirmState ? (
        <div className="vc-admin-dialog" role="dialog" aria-modal="true">
          <button type="button" className="vc-admin-drawer__backdrop" onClick={() => closeConfirm(false)} />
          <div className="vc-admin-dialog__panel">
            <h3>{confirmState.title}</h3>
            <p>{confirmState.message}</p>
            <div className="vc-admin-dialog__actions">
              <button type="button" className="vc-admin-btn vc-admin-btn--ghost" onClick={() => closeConfirm(false)}>
                {confirmState.cancelLabel ?? t('common.cancel')}
              </button>
              <button
                type="button"
                className={`vc-admin-btn${confirmState.danger ? ' vc-admin-btn--danger' : ' vc-admin-btn--primary'}`}
                onClick={() => closeConfirm(true)}
              >
                {confirmState.confirmLabel ?? t('admin.confirmOk')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {promptState ? (
        <div className="vc-admin-dialog" role="dialog" aria-modal="true">
          <button type="button" className="vc-admin-drawer__backdrop" onClick={() => closePrompt(null)} />
          <div className="vc-admin-dialog__panel">
            <h3>{promptState.title}</h3>
            {promptState.message ? <p>{promptState.message}</p> : null}
            <input
              autoFocus
              type="text"
              className="vc-admin-input"
              placeholder={promptState.placeholder}
              value={promptState.value}
              onChange={(e) => setPromptState({ ...promptState, value: e.target.value })}
            />
            <div className="vc-admin-dialog__actions">
              <button type="button" className="vc-admin-btn vc-admin-btn--ghost" onClick={() => closePrompt(null)}>
                {promptState.cancelLabel ?? t('common.cancel')}
              </button>
              <button
                type="button"
                className="vc-admin-btn vc-admin-btn--primary"
                onClick={() => closePrompt(promptState.value.trim() || null)}
              >
                {promptState.confirmLabel ?? t('admin.confirmOk')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </AdminUiContext.Provider>
  )
}
